// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Value = any

const RUNTIME_READ_ONLY_PROPERTY_DESCRIPTOR = {
  "configurable": false,
  "writable": false
}

const RUNTIME_PRIVATE_PROPERTY_DESCRIPTOR = {
  "enumerable": false,
  "configurable": false,
  "writable": false
}

// Single class for direct memory access in this package.
// Does:
// - DataView drop-in replacement
// - allow to provide an external, shared cache buffer
// - keep a pointer of the current location in memory
// - write n single-byte values sequentially
// - Unicode strings, ANSI strings, fixed length or null-terminated

export class DataArray {

  // takes a standard JavaScript ArrayBuffer
  // creates a view from byteOffset to length
  // has a pointer to keep track of the current position in the array
  // pointer can be initialized to a higher value than 0.
  //
  // Every time a DataArray instance is created, a new cache buffer
  // has to be allocated, or an existing array buffer can be provided


  public pointer: number
  public buffer: ArrayBuffer
  public byteOffset: number
  public byteLength: number
  public uint8Array: Uint8Array

  private cacheBuffer: ArrayBuffer
  private uint8CacheView: Uint8Array
  private int8CacheView: Int8Array
  private int16CacheView: Int16Array
  private int32CacheView: Int32Array


  constructor(
    buffer: ArrayBuffer,
    byteOffset = 0,
    length = buffer.byteLength - byteOffset,
    pointer = 0,
    cacheBuffer = new ArrayBuffer(8)
  ) {

    this.pointer = pointer

    this.uint8Array = new Uint8Array(buffer, byteOffset, length)

    this.buffer = this.uint8Array.buffer
    this.byteOffset = this.uint8Array.byteOffset
    this.byteLength = this.uint8Array.byteLength

    this.cacheBuffer = cacheBuffer
    this.uint8CacheView = new Uint8Array(this.cacheBuffer)
    this.int8CacheView = new Int8Array(this.cacheBuffer)
    this.int16CacheView = new Int16Array(this.cacheBuffer)
    this.int32CacheView = new Int32Array(this.cacheBuffer)

    Object.defineProperty(this, "pointer", { "configurable": false })

    for (const property of ["buffer", "byteOffset", "byteLength", "uint8Array", "per"])
      Object.defineProperty(this, property, RUNTIME_READ_ONLY_PROPERTY_DESCRIPTOR)

    for (const property of ["cacheBuffer", "uint8CacheView", "int8CacheView", "int16CacheView", "int32CacheView"])
      Object.defineProperty(this, property, RUNTIME_PRIVATE_PROPERTY_DESCRIPTOR)
  }


  write(...args: Value[]) {
    for (const value of args) this.writeUint8(value)
  }

  read() {
    return (this.readUint8())
  }


  writeUnicodeString(string: string) {
    for (let i = 0; i < string.length; i++) this.writeUint16(string.charCodeAt(i), true)
  }

  readUnicodeString(length = this.byteLength - this.byteOffset) {
    // read until null terminator is encountered or until provided length
    // If no length is provided, read to buffer completion
    //
    // length is in bytes, not chars
    let string = ""

    const start = this.pointer

    while (this.pointer < (length + start)) {
      const char = this.readUint16(true)
      if (char !== 0x0000) {
        string = string + String.fromCharCode(char)
      } else break
    }

    return (string)
  }


  writeANSIString(string: string) {
    for (let i = 0; i < string.length; i++) this.write(string.charCodeAt(i) & 0xFF)
  }

  readANSIString(length = this.byteLength - this.byteOffset) {
    // read until null terminator is encountered or until provided length
    // If no length is provided, read to buffer completion
    let string = ""

    const start = this.pointer

    while (this.pointer < (length + start)) {
      const char = this.read()
      if (char !== 0x00) {
        string = string + String.fromCharCode(char)
      } else break
    }

    return (string)
  }


  setUint8(index: number, value: Value) {
    if (index >= this.byteLength) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    this.uint8Array[index] = value
  }

  getUint8(index: number) {
    if (index >= this.byteLength) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)
    return (this.uint8Array[index])
  }

  writeUint8(value: Value) {
    this.setUint8(this.pointer++, value)
  }

  readUint8() {
    return (this.getUint8(this.pointer++))
  }


  setInt8(index: number, value: Value) {
    if (index >= this.byteLength) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    this.setUint8(index, value)
  }

  getInt8(index: number) {
    if (index >= this.byteLength) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    this.uint8CacheView[0] = this.uint8Array[index]
    return (this.int8CacheView[0])
  }

  writeInt8(value: Value) {
    this.setInt8(this.pointer++, value)
  }

  readInt8() {
    return (this.getInt8(this.pointer++))
  }


  setUint16(index: number, value: Value, littleEndian: boolean = false) {
    if (index >= this.byteLength - 1) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    if (littleEndian === false) {
      this.uint8Array[index] = value >>> 8
      this.uint8Array[index + 1] = value
    } else {
      this.uint8Array[index] = value
      this.uint8Array[index + 1] = value >>> 8
    }
  }

  getUint16(index: number, littleEndian: boolean = false) {
    if (index >= this.byteLength - 1) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    if (littleEndian === false) return ((this.uint8Array[index] << 8) | this.uint8Array[index + 1])
    else return (this.uint8Array[index] | (this.uint8Array[index + 1] << 8))
  }

  writeUint16(value: Value, littleEndian: boolean = false) {
    this.setUint16(this.pointer, value, littleEndian)
    this.pointer = this.pointer + 2
  }

  readUint16(littleEndian: boolean = false) {
    const value = this.getUint16(this.pointer, littleEndian)
    this.pointer = this.pointer + 2
    return (value)
  }


  setInt16(index: number, value: Value, littleEndian: boolean = false) {
    if (index >= this.byteLength - 1) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    this.setUint16(index, value, littleEndian)
  }

  getInt16(index: number, littleEndian: boolean = false) {
    if (index >= this.byteLength - 1) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    if (littleEndian === false) {
      this.uint8CacheView[0] = this.uint8Array[index + 1]
      this.uint8CacheView[1] = this.uint8Array[index]
    } else {
      this.uint8CacheView[0] = this.uint8Array[index]
      this.uint8CacheView[1] = this.uint8Array[index + 1]
    }
    return (this.int16CacheView[0])
  }

  writeInt16(value: Value, littleEndian: boolean = false) {
    this.setInt16(this.pointer, value, littleEndian)
    this.pointer = this.pointer + 2
  }

  readInt16(littleEndian: boolean = false) {
    const value = this.getInt16(this.pointer, littleEndian)
    this.pointer = this.pointer + 2
    return (value)
  }


  setUint32(index: number, value: Value, littleEndian: boolean = false) {
    if (index >= this.byteLength - 3) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    if (littleEndian === false) {
      this.uint8Array[index] = value >>> 24
      this.uint8Array[index + 1] = value >>> 16
      this.uint8Array[index + 2] = value >>> 8
      this.uint8Array[index + 3] = value
    } else {
      this.uint8Array[index] = value
      this.uint8Array[index + 1] = value >>> 8
      this.uint8Array[index + 2] = value >>> 16
      this.uint8Array[index + 3] = value >>> 24
    }
  }

  getUint32(index: number, littleEndian: boolean = false) {
    if (index >= this.byteLength - 3) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    if (littleEndian === false) {
      return (
        ((this.uint8Array[index] << 24) >>> 0)
        + ((this.uint8Array[index + 1] << 16)
          | (this.uint8Array[index + 2] << 8)
          | this.uint8Array[index + 3])
      )
    } else {
      return (
        (this.uint8Array[index]
          | (this.uint8Array[index + 1] << 8)
          | (this.uint8Array[index + 2] << 16))
        + ((this.uint8Array[index + 3] << 24) >>> 0)
      )
    }
  }

  writeUint32(value: number, littleEndian: boolean = false) {
    this.setUint32(this.pointer, value, littleEndian)
    this.pointer = this.pointer + 4
  }

  readUint32(littleEndian: boolean = false) {
    const value = this.getUint32(this.pointer, littleEndian)
    this.pointer = this.pointer + 4
    return (value)
  }


  setInt32(index: number, value: Value, littleEndian: boolean = false) {
    if (index >= this.byteLength - 3) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    this.setUint32(index, value, littleEndian)
  }

  getInt32(index: number, littleEndian: boolean = false) {
    if (index >= this.byteLength - 3) throw new Error(`Index (${index}) out of bounds (length: ${this.byteLength})`)

    if (littleEndian === false) {
      this.uint8CacheView[0] = this.uint8Array[index + 3]
      this.uint8CacheView[1] = this.uint8Array[index + 2]
      this.uint8CacheView[2] = this.uint8Array[index + 1]
      this.uint8CacheView[3] = this.uint8Array[index]
    } else {
      this.uint8CacheView[0] = this.uint8Array[index]
      this.uint8CacheView[1] = this.uint8Array[index + 1]
      this.uint8CacheView[2] = this.uint8Array[index + 2]
      this.uint8CacheView[3] = this.uint8Array[index + 3]
    }
    return (this.int32CacheView[0])
  }

  writeInt32(value: Value, littleEndian: boolean = false) {
    this.setInt32(this.pointer, value, littleEndian)
    this.pointer = this.pointer + 4
  }

  readInt32(littleEndian: boolean = false) {
    const value = this.getInt32(this.pointer, littleEndian)
    this.pointer = this.pointer + 4
    return (value)
  }
}
